import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { getProject, getUserGroup, getUserConfig } from '../../services/platformApi';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import Loading from '../../components/Loading';
import PlaceholderPage from '../Placeholder/PlaceholderPage';
import { getPageComponent, builtInPages } from '../registry';
import './ProjectPage.css';
import ipaConfig from "../../../app/ipaCore/ipaConfig";

export function ProjectPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { createCtx, isAuthenticated } = useAuth();
  const {
    selectedProject,
    selectedUserGroup,
    userConfig,
    applicationNamespaces,
    isLoadingScripts,
    setSelectedProject,
    setSelectedUserGroup,
    setUserConfig,
    storedState,
  } = useApp();

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Restore project and user group from storage if needed
  useEffect(() => {
    const restoreState = async () => {
      if (!projectId || !isAuthenticated) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setLoadError(null);

      try {
        const ctx = createCtx(applicationNamespaces);
        let project = selectedProject;
        let userGroup = selectedUserGroup;

        // Load project if not already loaded or different
        if (!project || project._id !== projectId) {
          console.log('Loading project:', projectId);
          project = await getProject(projectId, ctx);
          if (project) {
            setSelectedProject(project);
          } else {
            setLoadError('Project not found');
            navigate('/');
            return;
          }
        }

        // Load user group if we have a stored one and don't have it loaded
        if (!userGroup && storedState.userGroupId) {
          console.log('Loading user group:', storedState.userGroupId);
          userGroup = await getUserGroup(storedState.userGroupId, ctx);
          if (userGroup) {
            setSelectedUserGroup(userGroup);
          }
        }

        // Load user config if we have a user group and project but no config
        if (userGroup && project && !userConfig) {
          console.log('Loading user config for user group:', userGroup._name);
          const allNamespaces = [
            ...(userGroup._namespaces || []),
            ...(project._namespaces || []),
            ...applicationNamespaces
          ];
          const configCtx = createCtx(allNamespaces);
          const config = await getUserConfig(userGroup, project, configCtx);
          if (config) {
            console.log('User config loaded:', config.settings?.appTitle);
            // Fire and forget - state will update reactively
            setUserConfig(config);
          } else {
            console.warn('No user config found, using default');
            // Set a minimal default config so the page can render
            setUserConfig({
              handlers: {},
              groupedPages: {},
              settings: {
                appTitle: project._name || 'Project',
              }
            });
          }
        }

      } catch (error) {
        console.error('Failed to restore project state:', error);
        setLoadError('Failed to load project data');
      } finally {
        setIsLoading(false);
      }
    };

    restoreState();
  }, [projectId, isAuthenticated, applicationNamespaces]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build routes from user config
  const routes = useMemo(() => {
    if (!userConfig?.handlers) return [];

    return Object.entries(userConfig.handlers).map(([key, handler]) => {
      const PageComponent = getPageComponent(handler.pageComponent);

      return {
        key,
        path: handler.path,
        title: handler.title,
        icon: handler.icon,
        config: handler.config,
        handler: handler,
        componentPath: handler.pageComponent,
        Component: PageComponent, // Will be null if not found in registry
      };
    });
  }, [userConfig]);

  // Get default route from homepage config
  const defaultRoute = useMemo(() => {
    if (!userConfig?.homepage?.handler || !userConfig?.handlers) return null;

    const handler = userConfig.handlers[userConfig.homepage.handler];
    return handler?.path;
  }, [userConfig]);

  const handleLogoClick = () => {
    navigate('/');
  };

  // Render the appropriate component for a route
  const renderRouteElement = (route: typeof routes[0]) => {
    // If we have a real component, use it
    if (route.Component) {
      return <route.Component config={route.config} handler={route.handler} selectedItems={{ipaConfig: ipaConfig}} onLoadComplete={()=>{}   }/>;
    }

    // Otherwise show placeholder
    return (
      <PlaceholderPage
        title={route.title}
        icon={route.icon}
        componentPath={route.componentPath}
        config={route.config}
      />
    );
  };

  // Loading state (including script loading)
  if (isLoading || isLoadingScripts) {
    const message = isLoadingScripts ? 'Loading scripts...' : 'Loading project...';
    return <Loading message={message} fullScreen />;
  }

  // Error state
  if (loadError) {
    return (
      <div className="project-error">
        <h2>Error</h2>
        <p>{loadError}</p>
        <button onClick={() => navigate('/')}>Go to Home</button>
      </div>
    );
  }

  // Missing required data
  if (!selectedProject || !selectedUserGroup) {
    return (
      <div className="project-error">
        <h2>Missing Data</h2>
        <p>Please select a project and user group first.</p>
        <button onClick={() => navigate('/')}>Go to Home</button>
      </div>
    );
  }

  return (
    <div className="project-page">
      <Header onLogoClick={handleLogoClick} useProjectBranding={false} />

      <div className="project-layout">
        <Sidebar projectId={projectId!} />

        <main className="project-content">
          <Routes>
            {/* User config routes */}
            {routes.map(route => (
              <Route
                key={route.key}
                path={route.path}
                element={renderRouteElement(route)}
              />
            ))}

            {/* Built-in pages (always available) */}
            {builtInPages.map(page => (
              <Route
                key={page.key}
                path={page.path}
                element={<page.Component />}
              />
            ))}

            {/* Default redirect to homepage handler */}
            <Route
              path="/"
              element={
                defaultRoute ? (
                  <Navigate to={`.${defaultRoute}`} replace />
                ) : routes.length > 0 ? (
                  <Navigate to={`.${routes[0].path}`} replace />
                ) : (
                  <PlaceholderPage title="No pages configured" />
                )
              }
            />

            {/* Catch all - redirect to default */}
            <Route
              path="*"
              element={
                defaultRoute ? (
                  <Navigate to={`.${defaultRoute}`} replace />
                ) : (
                  <PlaceholderPage title="Page not found" />
                )
              }
            />
          </Routes>
        </main>
      </div>
    </div>
  );
}

export default ProjectPage;
